/**
 * Accounting Tracking Hooks
 */

import { useQuery } from '@tanstack/react-query';
import * as accountingService from '../api/services/accounting.service';
import type { AccountingSummary } from '../types/accounting';

const KEYS = {
  summary: ['accounting', 'summary'] as const,
  completed: ['accounting', 'completed'] as const,
  accepted: ['accounting', 'accepted'] as const,
  invoiced: ['accounting', 'invoiced'] as const,
  paid: ['accounting', 'paid'] as const,
  draft: ['accounting', 'draft'] as const,
};

interface UseAccountingListParams {
  offset?: number;
  limit?: number;
  search?: string;
  myOrders?: boolean;
}

export function useAccountingSummary(params: { myOrders?: boolean } = {}) {
  return useQuery<AccountingSummary>({
    queryKey: [...KEYS.summary, params],
    staleTime: 30_000,
    queryFn: async () => {
      const result = await accountingService.getAccountingSummary({
        my_orders: params.myOrders,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch summary');
      }
      return result.data!;
    },
  });
}

export function useAccountingCompleted(params: UseAccountingListParams = {}) {
  return useQuery({
    queryKey: [...KEYS.completed, params],
    queryFn: async () => {
      const result = await accountingService.getCompletedList({
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        my_orders: params.myOrders,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch completed list');
      }
      return result.data!;
    },
  });
}

export function useAccountingAccepted(params: UseAccountingListParams = {}) {
  return useQuery({
    queryKey: [...KEYS.accepted, params],
    queryFn: async () => {
      const result = await accountingService.getAcceptedList({
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        my_orders: params.myOrders,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch accepted list');
      }
      return result.data!;
    },
  });
}

export function useAccountingInvoiced(params: UseAccountingListParams = {}) {
  return useQuery({
    queryKey: [...KEYS.invoiced, params],
    queryFn: async () => {
      const result = await accountingService.getInvoicedList({
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        my_orders: params.myOrders,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch invoiced list');
      }
      return result.data!;
    },
  });
}

export function useAccountingPaid(params: UseAccountingListParams = {}) {
  return useQuery({
    queryKey: [...KEYS.paid, params],
    queryFn: async () => {
      const result = await accountingService.getPaidList({
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        my_orders: params.myOrders,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch paid list');
      }
      return result.data!;
    },
  });
}

export function useAccountingDraft(params: UseAccountingListParams = {}) {
  return useQuery({
    queryKey: [...KEYS.draft, params],
    queryFn: async () => {
      const result = await accountingService.getDraftList({
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        my_orders: params.myOrders,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch draft list');
      }
      return result.data!;
    },
  });
}
